import { useState, useEffect } from "react";
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, ArrowUpRight,
  ArrowDownRight, Clock, CheckCircle2, XCircle, Info
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function KpiCard({ label, value, format = "currency", tooltip, trend, trendLabel, color = "white", size = "default" }) {
  const formatted = format === "currency"
    ? `$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : value?.toLocaleString() || "0";
  const prefix = format === "currency" && value < 0 ? "-" : "";

  const colorMap = {
    white: "text-white",
    green: "text-emerald-400",
    red: "text-red-400",
    amber: "text-amber-400",
  };

  return (
    <div className={`bg-white/[0.02] border border-white/[0.06] rounded-xl ${size === "large" ? "p-6" : "p-4"}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between mb-1">
        <span className="text-[11px] font-medium text-white/40 uppercase tracking-wider">{label}</span>
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger>
                <Info className="w-3 h-3 text-white/20 hover:text-white/40 transition-colors" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs bg-[#000000] border border-white/10 text-white/70 text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <p className={`${size === "large" ? "text-3xl" : "text-2xl"} font-semibold ${colorMap[color] || colorMap.white} tracking-tight`}>
        {prefix}{formatted}
      </p>
      {trend !== undefined && (
        <div className="flex items-center gap-1 mt-1.5">
          {trend >= 0 ? (
            <ArrowUpRight className="w-3 h-3 text-emerald-400/70" />
          ) : (
            <ArrowDownRight className="w-3 h-3 text-red-400/70" />
          )}
          <span className={`text-[10px] ${trend >= 0 ? "text-emerald-400/70" : "text-red-400/70"}`}>
            {trendLabel || `${Math.abs(trend)}%`}
          </span>
        </div>
      )}
    </div>
  );
}

function AgingBar({ buckets }) {
  const total = Object.values(buckets).reduce((a, b) => a + b, 0);
  if (total === 0) return <div className="text-white/30 text-sm">No outstanding receivables</div>;

  const segments = [
    { key: "current", label: "Current", color: "bg-emerald-500" },
    { key: "1_15", label: "1-15d", color: "bg-yellow-500" },
    { key: "16_30", label: "16-30d", color: "bg-amber-500" },
    { key: "31_60", label: "31-60d", color: "bg-orange-500" },
    { key: "61_90", label: "61-90d", color: "bg-red-500" },
    { key: "90_plus", label: "90+", color: "bg-red-700" },
  ];

  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden mb-3 bg-white/[0.04]">
        {segments.map(seg => {
          const pct = (buckets[seg.key] || 0) / total * 100;
          if (pct < 0.5) return null;
          return (
            <div
              key={seg.key}
              className={`${seg.color} transition-all`}
              style={{ width: `${pct}%` }}
              title={`${seg.label}: $${(buckets[seg.key] || 0).toLocaleString()}`}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {segments.map(seg => (
          <div key={seg.key} className="text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <div className={`w-2 h-2 rounded-sm ${seg.color}`} />
              <span className="text-[10px] text-white/40">{seg.label}</span>
            </div>
            <p className="text-xs font-medium text-white/70">
              ${(buckets[seg.key] || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ExecutiveOverview({ connection }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API}/quickbooks/executive-overview`, { headers });
        if (!res.ok) {
          if (res.status === 400) {
            setError("no_data");
            return;
          }
          throw new Error("Failed to load");
        }
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
      </div>
    );
  }

  if (error === "no_data" || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center">
        <Clock className="w-10 h-10 text-white/20 mb-4" />
        <h3 className="text-lg font-medium text-white/60 mb-2">No Financial Data Yet</h3>
        <p className="text-white/30 text-sm max-w-md">
          Click the "Sync" button in the header to pull your QuickBooks data. First sync may take a moment.
        </p>
      </div>
    );
  }

  const { kpis, aging_buckets, overdue_invoices, alerts, ap_summary } = data;

  return (
    <div className="space-y-6" data-testid="executive-overview">
      {/* Primary KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard
          label="Cash Balance"
          value={kpis.cash_balance}
          color={kpis.cash_balance > 0 ? "green" : "red"}
          size="large"
          tooltip="Sum of all active bank account balances from QuickBooks"
        />
        <KpiCard
          label="A/R Outstanding"
          value={kpis.ar_outstanding}
          tooltip="Total unpaid invoice balances across all customers"
        />
        <KpiCard
          label="A/P Outstanding"
          value={kpis.ap_outstanding}
          tooltip="Total unpaid bill balances across all vendors"
        />
        <KpiCard
          label="Net Cash Position"
          value={kpis.net_cash_position}
          color={kpis.net_cash_position > 0 ? "green" : "red"}
          tooltip="Cash Balance minus Accounts Payable Outstanding"
        />
        <KpiCard
          label="Overdue Receivables"
          value={kpis.overdue_receivables}
          color={kpis.overdue_receivables > 0 ? "red" : "green"}
          tooltip="Sum of invoice balances past their due date"
        />
      </div>

      {/* Secondary KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Invoiced This Month" value={kpis.this_month_invoiced} tooltip="Total value of invoices created this month" />
        <KpiCard label="Collected This Month" value={kpis.this_month_collected} color="green" tooltip="Sum of payments received this month" />
        <KpiCard label="Expenses This Month" value={kpis.this_month_expenses} tooltip="Total value of bills created this month" />
        <KpiCard
          label="Net Operating Result"
          value={kpis.net_operating_result}
          color={kpis.net_operating_result >= 0 ? "green" : "red"}
          tooltip="Collections This Month minus Expenses This Month"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cash vs A/R vs A/P Panel */}
        <div className="lg:col-span-2 bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider">Cash vs Receivables vs Payables</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              { label: "Actual Cash", value: kpis.cash_balance, color: "emerald" },
              { label: "Total Receivables", value: kpis.ar_outstanding, color: "blue" },
              { label: "Overdue Receivables", value: kpis.overdue_receivables, color: "amber" },
              { label: "Payables Due", value: kpis.ap_outstanding, color: "orange" },
              { label: "Payables Overdue", value: ap_summary.overdue, color: "red" },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className={`h-1 rounded-full bg-${item.color}-500/40 mb-3`} />
                <p className="text-lg font-semibold text-white tracking-tight">
                  ${item.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
                <p className="text-[10px] text-white/40 mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>

          {/* Visual bars */}
          <div className="mt-6 space-y-2">
            {[
              { label: "Cash", value: kpis.cash_balance, max: Math.max(kpis.cash_balance, kpis.ar_outstanding, kpis.ap_outstanding), color: "bg-emerald-500" },
              { label: "A/R", value: kpis.ar_outstanding, max: Math.max(kpis.cash_balance, kpis.ar_outstanding, kpis.ap_outstanding), color: "bg-blue-500" },
              { label: "A/P", value: kpis.ap_outstanding, max: Math.max(kpis.cash_balance, kpis.ar_outstanding, kpis.ap_outstanding), color: "bg-orange-500" },
            ].map((bar, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-[11px] text-white/40 w-8">{bar.label}</span>
                <div className="flex-1 h-4 bg-white/[0.03] rounded-full overflow-hidden">
                  <div
                    className={`h-full ${bar.color} rounded-full transition-all duration-700`}
                    style={{ width: `${bar.max > 0 ? (bar.value / bar.max * 100) : 0}%` }}
                  />
                </div>
                <span className="text-xs text-white/50 w-24 text-right font-medium">
                  ${bar.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider">Executive Alerts</h3>
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500/40 mb-2" />
              <p className="text-sm text-white/40">All clear. No issues detected.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2.5 p-3 rounded-lg border ${
                    alert.type === "danger"
                      ? "bg-red-500/[0.06] border-red-500/20"
                      : "bg-amber-500/[0.06] border-amber-500/20"
                  }`}
                >
                  <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                    alert.type === "danger" ? "text-red-400" : "text-amber-400"
                  }`} />
                  <p className="text-xs text-white/70 leading-relaxed">{alert.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Collections Heatmap / Aging */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <h3 className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider">Receivables Aging</h3>
        <AgingBar buckets={aging_buckets} />
      </div>

      {/* Top Overdue Invoices */}
      {overdue_invoices.length > 0 && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider">
            Largest Overdue Invoices
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/30 text-[11px] uppercase tracking-wider">
                  <th className="text-left pb-3 font-medium">Invoice</th>
                  <th className="text-left pb-3 font-medium">Client</th>
                  <th className="text-right pb-3 font-medium">Amount</th>
                  <th className="text-right pb-3 font-medium">Days Overdue</th>
                  <th className="text-right pb-3 font-medium">Due Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {overdue_invoices.map((inv, i) => (
                  <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                    <td className="py-2.5 text-white/80 font-medium">{inv.invoice_num}</td>
                    <td className="py-2.5 text-white/60">{inv.customer}</td>
                    <td className="py-2.5 text-right text-white/80 font-medium">
                      ${inv.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-2.5 text-right">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        inv.days_overdue > 60 ? "bg-red-500/20 text-red-400"
                        : inv.days_overdue > 30 ? "bg-orange-500/20 text-orange-400"
                        : "bg-amber-500/20 text-amber-400"
                      }`}>
                        {inv.days_overdue}d
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-white/40 text-xs">{inv.due_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
